<!-- src/components/ReusePlanPreviewList.vue -->
<script setup lang="ts">
import { computed } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import type { Meal } from '../graphql/types'

dayjs.extend(utc)

interface Props {
  previewMeals: Meal[]
  existingMeals: Meal[]
  mealTypeNamesById?: Record<string, string>
}

const props = defineProps<Props>()

const getMealTypeName = (meal: Meal) => props.mealTypeNamesById?.[meal.type_id] || ''
const isMultiDay = (meal: Meal) => (meal.portions_days ?? 1) > 1

interface DayMeal {
  meal: Meal
  isPreview: boolean
}

interface DayGroup {
  date: string
  meals: DayMeal[]
}

const dayGroups = computed<DayGroup[]>(() => {
  const map = new Map<string, DayMeal[]>()

  const addMeals = (meals: Meal[], isPreview: boolean) => {
    for (const meal of meals) {
      if (!meal.scheduled_date) continue
      const dates = meal.scheduled_dates?.length ? meal.scheduled_dates : [meal.scheduled_date]
      for (const d of dates) {
        const dateKey = dayjs.utc(d).format('YYYY-MM-DD')
        if (!map.has(dateKey)) map.set(dateKey, [])
        map.get(dateKey)!.push({ meal, isPreview })
      }
    }
  }

  // Preview meals first within each day — they're what the user is here to check.
  addMeals(props.previewMeals, true)
  addMeals(props.existingMeals, false)

  return [...map.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([date, meals]) => ({ date, meals }))
})
</script>

<template>
  <div class="preview-list">
    <div v-if="dayGroups.length === 0" class="preview-list-empty">
      Meals will appear here once you pick meals and a target date.
    </div>

    <div v-for="group in dayGroups" :key="group.date" class="preview-day-group">
      <h4 class="preview-day-heading">{{ dayjs(group.date).format('ddd D MMM YYYY') }}</h4>

      <div
        v-for="{ meal, isPreview } in group.meals"
        :key="`${isPreview ? 'preview' : 'existing'}-${meal.id}`"
        class="preview-meal-row"
        :class="{ 'preview-meal-row--preview': isPreview }"
      >
        <span v-if="isPreview" class="preview-reused-tag">Reused</span>
        <span class="flex-1 min-w-0">
          <span class="flex items-center gap-2 flex-wrap">
            <span class="font-semibold text-[#0F172A]">{{ meal.title }}</span>
            <span v-if="getMealTypeName(meal)" class="category-badge category-main-dishes">{{ getMealTypeName(meal) }}</span>
          </span>
          <div
            v-if="isMultiDay(meal)"
            class="mt-1 meal-portions-label"
            :class="meal.portions_mode === 'repeat' ? 'meal-portions-label--repeat' : 'meal-portions-label--leftovers'"
          >
            <svg class="meal-portions-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                v-if="meal.portions_mode === 'repeat'"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
              <template v-else>
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z"
                />
              </template>
            </svg>
            <span>
              {{ meal.portions_days }}d ·
              {{ meal.portions_mode === 'repeat' ? 'Cook fresh each day' : 'Cooked Once' }}
            </span>
          </div>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.preview-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 420px;
  overflow-y: auto;
}
.preview-list-empty {
  text-align: center;
  padding: 32px 0;
  font-size: 14px;
  color: #64748b;
}
.preview-day-group {
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.preview-day-heading {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.01em;
}
.preview-meal-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  border-left: 3px solid transparent;
  font-size: 14px;
}
.preview-meal-row--preview {
  border-left-color: #4f8646;
  background-color: rgba(79, 134, 70, 0.06);
}
.preview-reused-tag {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #4f8646;
  padding-top: 2px;
}
.meal-portions-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 4px;
  letter-spacing: 0.01em;
}
.meal-portions-label--leftovers {
  background: var(--color-primary-50, #edf3ec);
  color: var(--color-primary-700, #3f4a3c);
}
.meal-portions-label--repeat {
  background: #eff6ff;
  color: #1d4ed8;
}
.meal-portions-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}
</style>
